"use client";

import WebsiteIcon from "@/icons/WebsiteIcon";
import { hasWebsiteIcon } from "@/icons/icon-catalogue";
import { useEffect, useState } from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { TeaserPopoverProps } from "@/cms/models";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faXmark } from "@fortawesome/sharp-light-svg-icons";
import { usePathname } from "next/navigation";

export const POPUP_CLOSED_KEY = "teaserPopupClosed";

/** `enabled: false` switches the teaser off; absent keeps the teaser every existing page already shows. */
const TeaserPopup: React.FC<TeaserPopoverProps> = (props) =>
  props.enabled === false ? null : <TeaserPopupContent {...props} />;

const TeaserPopupContent: React.FC<TeaserPopoverProps> = ({
  title,
  url,
  icon,
  iconId,
  delay,
  sectionId,
  hideIcon,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const path = usePathname();

  let targetUrl = url;
  if (sectionId) {
    targetUrl = `${path}?tab=${sectionId}`;
  }

  function scrollIntoView() {
    const caseContactSectionElement =
      document.getElementById("caseContactSection");
    if (caseContactSectionElement) {
      caseContactSectionElement.scrollIntoView({ behavior: "smooth" });
      window.dispatchEvent(
        new CustomEvent("selectedTabEvent", {
          detail: { selectedTab: "viewing" },
        }),
      );
      setIsOpen(false);
      return;
    }

    const caseTabsComponentElement =
      document.getElementById("caseTabsComponent");
    if (caseTabsComponentElement) {
      caseTabsComponentElement.scrollIntoView({ behavior: "smooth" });
      window.dispatchEvent(
        new CustomEvent("selectedTabEvent", {
          detail: { selectedTab: "viewing" },
        }),
      );
      setIsOpen(false);
      return;
    }
  }

  useEffect(() => {
    const hasBeenClosed = sessionStorage.getItem(POPUP_CLOSED_KEY);
    if (!hasBeenClosed) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [delay]);

  const onClose = () => {
    setIsOpen(false);
    sessionStorage.setItem(POPUP_CLOSED_KEY, "true");
  };

  return (
    <>
      {isOpen && (
        <motion.div
          className="fixed bottom-4 right-4 z-50"
          data-re-menu-companion
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
        >
          <div
            style={{
              backgroundColor: "var(--popup-bg-color)",
              color: "var(--popup-text-color)",
            }}
            className={`overflow-hidden shadow-lg homepage-button-rounded`}
          >
            <div className="relative flex flex-row justify-start gap-2 p-4 pr-10">
              <button
                onClick={() => onClose()}
                className="absolute right-2 top-2"
              >
                <FontAwesomeIcon className="size-6" icon={faXmark} />
                <span className="sr-only">Luk</span>
              </button>
              {!sectionId && (
                <Link
                  aria-label={title}
                  href={targetUrl}
                  className="flex flex-row justify-start gap-4 items-center"
                >
                  {hideIcon ? null : getIcon(icon, iconId)}
                  <div className="homepage-text-lg">{title}</div>
                </Link>
              )}
              {sectionId && (
                <button
                  aria-label={title}
                  onClick={() => scrollIntoView()}
                  className="flex flex-row justify-start gap-4 items-center"
                >
                  {hideIcon ? null : getIcon(icon, iconId)}
                  <div className="homepage-text-lg">{title}</div>
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default TeaserPopup;

function getIcon(icon: string, iconId?: unknown) {
  if (hasWebsiteIcon(iconId))
    return (
      <WebsiteIcon
        name={iconId}
        className="size-8 text-[var(--popup-text-color)]"
      />
    );
  switch (icon) {
    case "calendar":
      return (
        <FontAwesomeIcon
          className="size-8 text-[var(--popup-text-color)]"
          icon={faCalendarDays}
        />
      );
    default:
      return (
        <FontAwesomeIcon
          className="size-8 text-[var(--popup-text-color)]"
          icon={faCalendarDays}
        />
      );
  }
}
